import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardMedia,
    CircularProgress,
    Grid,
    IconButton,
    Input,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Stepper from "../settings/Stepper";
import { useNavigate } from "react-router-dom";
import {
  CalendarIcon,
  CameraIconGrey,
  ChevBackIcon,
  CloseIcon,
  ContractDownloadIcon,
  MediaIcon,
  UploadIcon,
} from "../../assets/icons/icons";
import InputText from "../../components/InputText";
import { SelectField } from "../../components/SelectField";
import CommonButton from "../../components/CommonButton";
import PlusIcon from "@mui/icons-material/Add";
import {
  ArrowForward,
  HighlightOff,
  Remove,
  RemoveCircle,
} from "@mui/icons-material";
import { useGetDailyReportFormData, useGetProjectsAll } from "../projects/hooks";
import {
  useGetItems,
  useGetReportNumberExists,
  useMutateCreateDailyReport,
} from "../projects/daily-reports/hooks";
import { getCurrentDateTime } from "../../lib/time";
import dayjs from "dayjs";
import { useGetTimeSheetsByDate } from "../projects/timesheets/hooks";
import { debounce } from "lodash";
import { Fetching } from "../../components/Fetching";
import { formatFileSize } from "react-papaparse";
import { DatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const steps = ["Details", "Work Log", "Equipment", "Photos", "Confirmation"];
interface Entry {
  craft: string;
  hoursWorked: string;
  hrCode: string;
  description: string;
}
const types = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/tiff",
  "image/svg+xml",
];

const typographyStyle = {
  fontSize: "16px",
  color: "#5A525B",
  paddingBottom: 0.5,
  fontWeight: "500",
  fontFamily: "Rza",
};

const DailyReport: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(0);
  const fillPercentages =
    activeStep === 0
      ? "20%"
      : activeStep === 1
      ? "40%"
      : activeStep === 2
      ? "60%"
      : activeStep === 3
      ? "80%"
      : activeStep === 4
      ? "100%"
      : "";
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [entries, setEntries] = useState<Entry[]>([]);
  const [materials, setMaterials] = useState<{ lineItemValue: string }[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const [cameraModal, setCameraModal] = useState<boolean>(false);
  const [projectdata, setprojectdata] = useState<any>({});
  const [projectId, setprojectId] = useState<string>("");
  const [reportDate, setReportDate] = useState<any>(getCurrentDateTime());
  const [reportData, setReportData] = useState<any>(null);
  const [reportExistError, setReportExistError] = useState<any>(null);
  const [projectNames, setProjectName] = useState<string>("");
  const [equipments, setEquipments] = useState<{ lineItemValue: string }[]>([
    { lineItemValue: "" },
  ]);
  const [selectproject, setselectproject] = useState<boolean>(false);
  const [isReportNumberExistsFetching, setIsReportNumberExistsFetching] =
    useState(false);
  const [causingDelaysProgress, setCausingDelaysProgress] = useState<
    { lineItemValue: string }[]
  >([{ lineItemValue: "" }]);
  const [formData, setFormData] = useState({
    project: "",
    date: reportDate,
    generalContractor: "",
    weather: projectdata?.weather,
    craft: "",
    hoursWorked: "",
    hrCode: "",
    description: "",
    supplyerName: "",
    equipment: { lineItemValue: "" },
    deliveredMaterials: "",
    workPerformedToday: "",
    causingDelaysProgress: [],
    foreman: "",
    siteManager: "",
    supervisor: "",
    inspector: "",
  });
  const { data: ReportFormData, isLoading: isLoadingFormData } =
  useGetDailyReportFormData('projectReportFormData', projectId)
  const [reportNumber, setReportNumber] = useState<any>();
  const { data: reportDataa } = useGetItems("daily-reports", projectId);
  const { mutateAsync: createDailyReport, isLoading: isLoadingMutation } =
    useMutateCreateDailyReport("dailyreports");
  const { data: projects, error, refetch } = useGetProjectsAll("projects");
  const { data: reportNumberExists, isFetching: isFetchingReportNumber } =
    useGetReportNumberExists("reportNumber", reportNumber);
  const generateNextReportNumber = () => {
    if (reportDataa && reportDataa.length > 0) {
      const highestReportNumber = Math.max(
        ...reportDataa.map((report: any) => report.reportNumber),
        0
      );
      return highestReportNumber + 1;
    }
    return 1;
  };
  useEffect(() => {
    if (reportDataa && reportDataa.length > 0) {
      reportDataa.map((item: any) => {
        setprojectdata(item);
      });
    }
    onChangeHandler(reportData);
  }, [reportDataa]);

  useEffect(() => {
    setFormData({ ...formData, weather: projectdata?.weather });
  }, [projectdata]);

  const onChangeHandler = (data: any) => {
    if (data?.hasOwnProperty("date")) {
      setReportDate(data.date);
    }
    if (data?.reportNumber) {
      const sanitizedReportNumber = String(
        data?.reportNumber?.toString()
      )?.replace(/\D/g, "");
      if (sanitizedReportNumber.length > 10) {
        data.reportNumber = sanitizedReportNumber.slice(0, 10);
      } else {
        data.reportNumber = sanitizedReportNumber;
      }
      handleSearchingReportNumber(data.reportNumber);
    }
  };
  useEffect(() => {
    setReportNumber(generateNextReportNumber());
  }, [reportDataa]);

  const submitHandler = async () => {
    const formData = new FormData()
    let itExists = reportDataa?.findIndex(
      (report: any) => report.reportNumber === Number(reportData?.reportNumber)
    );
    if (itExists !== -1) {
      setReportExistError("The report ID already exists!");
      return;
    } else {
      setReportExistError(null);
    }
    const dailyReport = {
      ...reportData,
      project: projectId,
      date: dayjs(reportDate).format("YYYY-MM-DD"),
      finalized: true,
      clientEmails: projectdata.clientEmails,
      account: projectdata.account,
    };
  formData.append('dailyReport', JSON.stringify(dailyReport))
    selectedFiles.forEach((file, index) => {
    formData.append('image', file); 
  })
    const result = await createDailyReport(formData);
    if (result) {
      navigate(`/projects/${projectdata._id}/daily-reports/${result._id}`);
    }
  };

  useEffect(() => {
    if (reportNumber) {
      if (reportNumberExists?.exists) {
        setReportNumber(parseInt(reportNumber) + 1);
      } else {
        setReportData({
          ...reportData,
          reportNumber: parseInt(reportNumber),
        });
      }
      setIsReportNumberExistsFetching(false);
    } else {
      setReportNumber(null);
    }
  }, [reportNumberExists, formData]);

  const handleSearchingReportNumber = useCallback(
    debounce((number: string) => {
      number !== reportNumber && setIsReportNumberExistsFetching(true);
      setReportNumber(number);
    }, 500),
    []
  );

  useEffect(() => {
    const payload = {
      account: projectdata.account,
      projectNumber: projectdata.projectNumber,
      reportNumber: reportNumber,
      date: formData.date,
      project: {
        ...projectdata,
      },
      activityLocation: projectdata.activitylocation,
      weather: formData.weather,
      clientName: projectdata.clientName,
      service: projectdata.service,
      timesheets: null,
      equipment: equipments,
      materialsDelivered: materials,
      workPerformed: formData.workPerformedToday,
      delaysAndImpacts: causingDelaysProgress,
      foreman: formData.foreman,
      projectManager: formData.siteManager,
      image: selectedFiles,
      workLog: entries,
      foremanSignature: formData.foreman,
      projectManagerSignature: formData.siteManager,
      finalized: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setReportData(payload);
  }, [formData]);

  const handleNext = () => {
    if (activeStep === 4) {
      submitHandler();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleEquipmentChange = (index: number, newValue: string) => {
    const newEquipments = [...equipments];
    newEquipments[index] = { lineItemValue: newValue };
    setEquipments(newEquipments);
  };

  const handleAddEquipment = () => {
    setEquipments([...equipments, { lineItemValue: "" }]);
  };

  const handleRemoveEquipment = (index: number) => {
    if (equipments.length > 1) {
      const newEquipments = equipments.filter((_, i) => i !== index);
      setEquipments(newEquipments);
    }
  };
  const handleCausingDelaysChange = (index: number, newValue: string) => {
    const newDelaysProgress = [...causingDelaysProgress];
    newDelaysProgress[index] = { lineItemValue: newValue };
    setCausingDelaysProgress(newDelaysProgress);
  };
  const handleAddCausingDelays = () => {
    setCausingDelaysProgress([...causingDelaysProgress, { lineItemValue: "" }]);
  };
  const handleRemoveCausingDelays = (index: number) => {
    if (causingDelaysProgress.length > 1) {
      const newDelaysProgress = causingDelaysProgress.filter(
        (_, i) => i !== index
      );
      setCausingDelaysProgress(newDelaysProgress);
    }
  };

  // Handle Remove Entry
  const handleRemoveEntry = (index: number) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      const acceptedTypes = types;
      // const isValidFileType = fileArray.every((file) =>
      //   acceptedTypes.includes(file.type)
      // );
      // if (!isValidFileType) {
      //   alert("Please select only images (jpeg, png) or videos (mp4, mpeg).");
      //   return;
      // }
      setSelectedFiles((prevFiles) => [...prevFiles, ...fileArray]);

      const previews = fileArray.map((file) => URL.createObjectURL(file));
      setImagePreviews((prevPreviews) => [...prevPreviews, ...previews]);
      setFileUploaded(true);
    }
  };

  const removeFile = (fileIndex: number) => {
    const newSelectedFiles = [...selectedFiles];
    const newImagePreviews = [...imagePreviews];
    URL.revokeObjectURL(newImagePreviews[fileIndex]);
    newSelectedFiles.splice(fileIndex, 1);
    newImagePreviews.splice(fileIndex, 1);
    setSelectedFiles(newSelectedFiles);
    setImagePreviews(newImagePreviews);
    if (newSelectedFiles.length === 0) {
      setFileUploaded(false);
    }
  };

  const handleAddEntry = () => {
    if (
      formData.craft.trim() !== "" &&
      formData.hoursWorked.trim() !== "" &&
      formData.hrCode.trim() !== "" &&
      formData.description.trim() !== ""
    ) {
      setEntries([
        ...entries,
        {
          craft: formData.craft,
          hoursWorked: formData.hoursWorked,
          hrCode: formData.hrCode,
          description: formData.description,
        },
      ]);
      setFormData({
        ...formData,
        craft: "",
        hoursWorked: "",
        hrCode: "",
        description: "",
      });
    } else {
      console.log('Please fill out all fields before adding an entry.')
      // Optionally, you could show an error message or handle the case when fields are empty
      // alert("Please fill out all fields before adding an entry.");
    }
  };

  const handleAddMaterials = () => {
    if (formData.deliveredMaterials.trim() !== "") {
      setMaterials([
        ...materials,
        { lineItemValue: formData.deliveredMaterials },
      ]);
      // Clear the form field
      setFormData({ ...formData, deliveredMaterials: "" });
    }
  };

  const handleRemoveMaterials = (index: number) => {
    const newMaterials = materials.filter((_, i) => i !== index);
    setMaterials(newMaterials);
  };
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const fileArray = Array.from(files);
      const acceptedTypes = types;
      // const isValidFileType = fileArray.every((file) =>
      //   acceptedTypes.includes(file.type)
      // );
      // if (!isValidFileType) {
      //   alert("Please select only images or videos.");
      //   return;
      // }
      setSelectedFiles((prevFiles) => [...prevFiles, ...fileArray]);
      const previews = fileArray.map((file) => URL.createObjectURL(file));
      setImagePreviews((prevPreviews) => [...prevPreviews, ...previews]);
      setFileUploaded(true);
    }
  };
  if (
    isLoadingMutation
  ) {
    return <Fetching />
  }

  return (
    <Box sx={{ p: 2 }}>
      <IconButton
        sx={{
          background: "#24212C12",
          borderRadius: 1.8,
          padding: "6px 10px",
        }}
        onClick={() => navigate("/projects")}
      >
        <ChevBackIcon />
        <Typography variant="caption" sx={{ ml: 1 }}>
          Go Back
        </Typography>
      </IconButton>
      <Typography
        variant="h4"
        sx={{ fontWeight: "500", mt: 2, mb: 2, fontFamily: "Rza" }}
      >
        Daily Report for - {projectNames}
         {/* : 00{reportNumber} */}
      </Typography>

      <Card sx={{ p: 2, borderRadius: "8px" }}>
        {/* {isReportNumberExistsFetching && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.5rem',
                  }}>
                  <CircularProgress size='1rem' />
                  <div style={{ marginLeft: '0.5rem' }}>
                    Verifying reportNumber...
                  </div>
                </div>
              )} */}
        {reportExistError && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {reportExistError}
          </Alert>
        )}
        <Stepper
          steps={steps}
          activeStep={activeStep}
          fillPercentages={fillPercentages}
        />
        {activeStep === 0 && (
          <Grid container spacing={2} mt={2}>
            <Grid item xs={6}>
              <Box>
                <Typography sx={typographyStyle}>Select Project</Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  border: "1px solid #DBDBDB",
                  padding: 0.8,
                }}
              >
                <SelectField
                  value={formData["project"] || ""}
                  onChange={(e: any, selectedOption: any) => {
                    setselectproject(!!e.target.value);
                    setFormData({ ...formData, project: e.target.value });
                    setProjectName(selectedOption?.projectName);
                    setprojectId(e.target.value);
                  }}
                  options={projects}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
            <Typography
                    sx={{  fontSize: '16px',
                      color: '#5F5C5F',
                      paddingBottom: 0.5,
                      fontWeight: '500',
                      fontFamily: "Buenos Aires",}}>
                    Date
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: 0.9,
                      border: '1px solid #E5E3EA',
                      width: '100%',
                    }}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label=''
                        onChange={(e: any) => {
                          setFormData({ ...formData, date: e });
                        }}
                        value={formData["date"] ?? ""}
                        renderInput={(params: any) => {
                          return (
                            <TextField
                              {...params}
                              required
                              value={params.inputProps.value}
                              type={params.inputProps.type}
                              placeholder={params.inputProps.placeholder}
                              onChange={params.inputProps.onChange}
                              className={params.className}
                              inputProps={{
                                ...params,
                                readOnly: true,
                              }}
                              onClick={(event: any) => {
                                // @ts-ignore
                                params.InputProps.endAdornment.props.children.props.onClick()
                              }}
                              name='dailyEndTime'
                              variant='standard'
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <IconButton
                                    {...params.InputProps}
                                    // @ts-ignore
                                    onClick={(event: any) => {
                                      // @ts-ignore
                                      params.InputProps.endAdornment.props.children.props.onClick()
                                    }}>
                                    <CalendarIcon />
                                  </IconButton>
                                ),
                              }}
                              sx={{
                                width: '100%',
                                '& .MuiInput-underline:before': {
                                  display: 'none',
                                },
                                '& .MuiInput-underline:hover:not(.Mui-disabled):before':
                                  {
                                    display: 'none',
                                  },
                                '& .MuiInput-underline:after': {
                                  display: 'none',
                                },
                              }}
                            />
                          )
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
              {/* <InputText
                name={"date"}
                value={formData["date"]}
                onChange={(e: any) => {
                  setFormData({ ...formData, date: e });
                }}
                error={false}
                label="Date"
                required
              /> */}
            </Grid>
            <Grid item xs={6}>
              <InputText
                fieldname={"General Contractor"}
                value={formData["generalContractor"] || ""}
                onChange={(e: any) =>
                  setFormData({ ...formData, generalContractor: e })
                }
                error={false}
                label="General Contractor"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography sx={typographyStyle}>Weather Condition</Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  border: "1px solid #DBDBDB",
                  padding: 0.8,
                }}
              >
                <SelectField
                  value={formData["weather"] || ""}
                  onChange={(e: any) => {
                    setFormData({ ...formData, weather: e.target.value });
                  }}
                  options={ReportFormData?.weather ?? ""}
                />
              </Box>
              {/* <InputText
                fieldname={"Weather"}
                value={formData["weather"] || ""}
                onChange={(e: any) => setFormData({ ...formData, weather: e })}
                error={false}
                label="Weather Condition"
                required
              /> */}
            </Grid>
          </Grid>
        )}

        {activeStep === 1 && (
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={3}>
                  <InputText
                    value={formData.craft || ""}
                    onChange={(e: any) =>
                      setFormData({ ...formData, craft: e })
                    }
                    error={false}
                    label="Craft (e.g., Carpentry, Plumbing)"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={1.5}>
                  <InputText
                    value={formData.hoursWorked || ""}
                    onChange={(e: any) =>
                      setFormData({ ...formData, hoursWorked: e })
                    }
                    error={false}
                    label="Hours Worked"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={1.5}>
                  <InputText
                    value={formData.hrCode || ""}
                    onChange={(e: any) =>
                      setFormData({ ...formData, hrCode: e })
                    }
                    error={false}
                    label="HR Code"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={3}>
                  <InputText
                    value={formData.description || ""}
                    onChange={(e: any) =>
                      setFormData({ ...formData, description: e })
                    }
                    error={false}
                    label="Description of Work"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={3} sx={{ mt: 3.5 }}>
                  <Box>
                    <CommonButton
                      icon={PlusIcon}
                      label="Add Entry"
                      onClick={handleAddEntry}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={7} sx={{ mt: 3 }}>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                  <TableBody
                    sx={{
                      "& td": {
                        backgroundColor: "rgba(241, 239, 245, 0.5)",
                        color: "#5F5C5F",
                        fontWeight: 400,
                        whiteSpace: "nowrap",
                        fontSize: 14,
                        padding: "12px 0px",
                        height: "20px",
                        textAlign: "start",
                      },
                    }}
                  >
                    {entries.map(
                      (
                        entry: {
                          craft: string;
                          hoursWorked: string | number;
                          hrCode: string | number;
                          description: string;
                        },
                        index: number
                      ) => (
                        <TableRow key={index}>
                          <TableCell>{entry?.craft}</TableCell>
                          <TableCell>{entry?.hoursWorked}</TableCell>
                          <TableCell>{entry?.hrCode}</TableCell>
                          <TableCell>
                            {entry?.description.length > 50
                              ? entry?.description.slice(0, 50) + "..."
                              : entry?.description}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              sx={{ float: "right" }}
                              onClick={() => handleRemoveEntry(index)}
                              color="error"
                            >
                              <HighlightOff sx={{ color: "black" }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}

        {activeStep === 2 && (
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12}>
              <Box>
                <Typography sx={typographyStyle}>Supplier Name</Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  border: "1px solid #DBDBDB",
                  padding: 0.8,
                }}
              >
                <SelectField
                  id="demo-simple-select"
                  value={formData.supplyerName || ""}
                  onChange={(e: any) =>
                    setFormData({ ...formData, supplyerName: e.target.value })
                  }
                  options={[]}
                  placeholder="Black and Rounds"
                />
              </Box>
            </Grid>

            {equipments.map((equipment, index) => (
              <>
                <Grid item xs={9}>
                  <Box sx={{ mt: 2 }}>
                    <Box key={index} sx={{ mb: 2 }}>
                      <InputText
                        value={equipment.lineItemValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          // @ts-ignore
                          handleEquipmentChange(index, e)
                        }
                        error={false}
                        label={`Equipment Details ${index + 1}`}
                        required
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={12} md={3} sx={{ mt: 5.5 }}>
                  {index > 0 ?
                    <CommonButton
                      label="Remove"
                      startIcon={<Remove />}
                      onClick={() => handleRemoveEquipment(index)}
                    /> :
                    <CommonButton
                      label="Add Entry"
                      startIcon={<PlusIcon />}
                      onClick={handleAddEquipment}
                    />
                  }
                </Grid>
              </>
            ))}

            <Grid item xs={9}>
              <Box>
                <InputText
                  value={formData.deliveredMaterials || ""}
                  onChange={(e: any) =>
                    setFormData({ ...formData, deliveredMaterials: e })
                  }
                  error={false}
                  label="Delivered Materials"
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={3} sx={{ mt: 3.5 }}>
              <Box>
                <CommonButton
                  icon={PlusIcon}
                  label="Add Entry"
                  onClick={handleAddMaterials}
                />
              </Box>
            </Grid>
            <Grid item xs={4} sm={3} mt={1}>
              <TableContainer component={Paper}>
                <Table>
                  <TableBody
                    sx={{
                      "& td": {
                        backgroundColor: "rgba(241, 239, 245, 0.5)",
                        color: "#5F5C5F",
                        fontWeight: 400,
                        whiteSpace: "nowrap",
                        fontSize: 14,
                        padding: "0px 10px",
                        textAlign: "start",
                      },
                    }}
                  >
                    {materials.map((material, index) => (
                      <TableRow key={index}>
                        <TableCell>{material.lineItemValue}</TableCell>
                        <TableCell>
                          <IconButton
                            sx={{ float: "right" }}
                            onClick={() => handleRemoveMaterials(index)}
                            color="error"
                          >
                            <HighlightOff sx={{ color: "black" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <InputText
                  value={formData.workPerformedToday || ""}
                  onChange={(e: any) =>
                    setFormData({ ...formData, workPerformedToday: e })
                  }
                  error={false}
                  label="List work performed today. Include any special conditions, and comments (Delays, conflicts, Hold-ups, etc)"
                  required
                  multiline={true}
                  rows={5}
                />
              </Box>
            </Grid>
            {causingDelaysProgress.map((progress, index) => (
              <>  
                <Grid item xs={12} sm={12} md={9}>
                  <Box sx={{ mt: 2 }}>
                    <Box key={index} sx={{ }}>
                      <InputText
                        value={progress.lineItemValue}
                        onChange={(e: any) => handleCausingDelaysChange(index, e)}
                        error={false}
                        label="All items listed below are causing a delay(s) to DMS' progress & following activities. Time/Cost impacts will be addressed at later date."
                        required
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={12} md={3} sx={{display:'flex', alignItems:'end', mb:2}}>
                  {index > 0 ?
                    <CommonButton
                      label="Remove"
                      startIcon={<Remove />}
                      onClick={() => handleRemoveCausingDelays(index)}
                    /> :
                    <CommonButton
                      label="Add"
                      startIcon={<PlusIcon />}
                      onClick={handleAddCausingDelays}
                    />
                  }
                </Grid>
              </>
            ))}
          </Grid>
        )}

        {activeStep === 3 && (
          <>
            <Box
              sx={{
                border: "1px dashed #6B646CAC",
                borderRadius: "8px",
                padding: "4rem 1rem",
                textAlign: "center",
                height: 294,
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                flexDirection: "column",
                m: 4,
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <Typography
                fontSize="16px"
                paddingLeft={2.5}
                paddingRight={2.5}
                fontWeight="400"
                color="#5A525B"
                mb={2}
                fontFamily={"Buenos Aires"}
              >
                Update cost codes. Click select to upload
                <br /> or drag and drop your files
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "15px",
                }}
              >
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <IconButton
                  sx={{
                    background: "#F0845D",
                    "&:hover": {
                      background: "#F0845D",
                    },
                    borderRadius: 1.8,
                    padding: "6px 10px",
                  }}
                  disabled={isLoading}
                  onClick={() => {
                    const input = document.getElementById(
                      "file-upload"
                    ) as HTMLInputElement;
                    input?.click();
                  }}
                >
                  <UploadIcon />
                  <Typography
                    component="p"
                    sx={{
                      fontSize: 12,
                      paddingLeft: 1,
                      color: "white",
                      margin: 0,
                    }}
                  >
                    Select File
                  </Typography>
                </IconButton>
                <input
                  type="file"
                  id="media-library"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept="image/png, image/jpeg"
                />
                <IconButton
                  sx={{
                    background: "#59A0A0",
                    "&:hover": {
                      background: "#59A0A0",
                    },
                    borderRadius: 1.8,
                    padding: "6px 10px",
                    marginLeft: 1,
                  }}
                  disabled={isLoading}
                  // onClick={() => setCameraModal(true)}
                  onClick={() => {
                    const input = document.getElementById(
                      "media-library"
                    ) as HTMLInputElement;
                    input?.click();
                    
                  }}
                >
                  <MediaIcon />
                  <Typography
                    component="p"
                    sx={{
                      fontSize: 12,
                      paddingLeft: 1,
                      margin: 0,
                    }}
                  >
                    Media Library
                  </Typography>
                </IconButton>
                <IconButton
                  sx={{
                    background: "#E6E4EA",
                    "&:hover": {
                      background: "#E6E4EA",
                    },
                    borderRadius: 1.8,
                    padding: "6px 10px",
                    marginLeft: 1,
                  }}
                  disabled={isLoading}
                  onClick={() => setCameraModal(true)}
                >
                  <CameraIconGrey />
                  <Typography
                    component="p"
                    sx={{
                      fontSize: 12,
                      paddingLeft: 1,
                      margin: 0,
                    }}
                  >
                    Open Camera
                  </Typography>
                </IconButton>
              </Box>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent={"flex-start"}
              flexDirection={"row"}
              overflow={"scroll"}
              paddingBottom={2}
              sx={{
                "::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              {selectedFiles.map((file, index) => (
                imagePreviews[index] && /\.(png|jpe?g|gif)$/i.test(file.name) ?(
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mr: 2,
                    width: 60,
                    height: 90,
                    position: "relative",
                  }}
                >
                  <CardMedia
                    component="img"
                    sx={{
                      width: 60,
                      height: 90,
                      objectFit: "cover",
                      backgroundColor: "#E6E4EA",
                      borderRadius: "8px",
                    }}
                    image={imagePreviews[index]}
                    alt={file.name}
                  />
                  {isLoading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  )}
                  {!isLoading && (
                    <IconButton
                      onClick={() => removeFile(index)}
                      sx={{
                        position: "absolute",
                        bottom: -15,
                        right: -15,
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Box>
                ):(
                  <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 2,
                  }}
                >
                  <ContractDownloadIcon />
                  <Typography
                    sx={{
                      color: '#5F5C5F',
                      mr: 1,
                      ml: 1,
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#5A525B80',
                    }}
                  >
                    {formatFileSize(file.size) + 'kb'}
                  </Typography>
                </Box>
                )
              ))}
            </Box>
          </>
        )}

        {activeStep === 4 && (
          <Grid container spacing={2} mt={2}>
            <Grid item xs={6}>
              <Box>
                <InputText
                  value={formData.foreman || ""}
                  onChange={(e: any) =>
                    setFormData({ ...formData, foreman: e })
                  }
                  error={false}
                  label="Foreman Name"
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <InputText
                  value={formData.siteManager || ""}
                  onChange={(e: any) =>
                    setFormData({ ...formData, siteManager: e })
                  }
                  error={false}
                  label="Site Manager Name"
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <InputText
                  value={formData.supervisor || ""}
                  onChange={(e: any) =>
                    setFormData({ ...formData, supervisor: e })
                  }
                  error={false}
                  label="Supervisor Name"
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <InputText
                  value={formData.inspector || ""}
                  onChange={(e: any) =>
                    setFormData({ ...formData, inspector: e })
                  }
                  error={false}
                  label="Inspector Name"
                  required
                />
              </Box>
            </Grid>
          </Grid>
        )}
        {/* Navigation Buttons */}
        <Box sx={{ marginTop: 1 }}>
          <Button
            variant="contained"
            sx={{
              mr: 1,
              backgroundColor: "#EEEEEE",
              "&:hover": { backgroundColor: "#EEEEEE" },
              color: "#24212C",
              textTransform: "capitalize",
              fontWeight: "500",
              fontSize: "13px",
              boxShadow: "none",
              // fontFamily: 'Circular Std',
              px: 3,
              borderRadius: 1.5,
              paddingTop: 1.1,
              paddingBottom: 1.1,
            }}
            onClick={handleBack}
            color="primary"
          >
            Cancel
          </Button>
          {activeStep === 4 ? (
            ""
          ) : (
            <Button
              variant="contained"
              sx={{
                mr: 1,
                color: "white",
                backgroundColor: "#59A0A0",
                "&:hover": {
                  backgroundColor: "#59A0A0",
                },
                textTransform: "capitalize",
                fontWeight: "500",
                fontSize: "13px",
                boxShadow: "none",
                fontFamily: "sans-serif",
                px: 3,
                borderRadius: 1.5,
                paddingTop: 1.1,
                paddingBottom: 1.1,
              }}
            >
              Save
            </Button>
          )}
          <Button
            variant="contained"
            sx={{
              mr: 1,
              color: "white",
              "&:hover": {
                backgroundColor: "",
              },
              textTransform: "capitalize",
              fontWeight: "500",
              fontSize: "13px",
              boxShadow: "none",
              // fontFamily: 'Circular Std',
              px: 3,
              borderRadius: 1.5,
              paddingTop: 1.1,
              paddingLeft: 10,
              paddingRight: 10,
              paddingBottom: 1.1,
            }}
            disabled = {selectproject == false}
            onClick={handleNext}
          >
            <ArrowForward sx={{ fontSize: "16px", mr: "3px" }} /> Next
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default DailyReport;
